import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Droplets, Plus, Minus, Target, Trophy, RefreshCw, Settings2, X } from "lucide-react";
import { toast } from "sonner";

// Hydration Tracker Component
// Tracks daily water intake with visual feedback and goals
const PRESET_GOALS = [32, 64, 96, 128, 160]; // oz options
const PRESET_GLASS = [4, 8, 12, 16, 20, 24, 32]; // oz options

const HydrationTracker = ({
  compact = false,
  showGoal = true,
  dailyGoalOz: defaultGoalOz = 128, // Default 1 gallon (128 oz)
  onUpdate = () => {},
  className = ""
}) => {
  const [waterIntake, setWaterIntake] = useState(0); // in oz
  const [lastDrink, setLastDrink] = useState(null);
  const [glassSize, setGlassSize] = useState(8); // oz per glass
  const [dailyGoalOz, setDailyGoalOz] = useState(defaultGoalOz);
  const [showSettings, setShowSettings] = useState(false);
  const [showCustomAdd, setShowCustomAdd] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [customGoalInput, setCustomGoalInput] = useState("");

  // Load saved hydration data + user prefs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const today = new Date().toDateString();
    const savedData = localStorage.getItem('hydrationData');
    const savedGoal = parseInt(localStorage.getItem('hydrationGoalOz'), 10);
    const savedGlass = parseInt(localStorage.getItem('hydrationGlassOz'), 10);

    if (savedGoal && savedGoal > 0) setDailyGoalOz(savedGoal);
    if (savedGlass && savedGlass > 0) setGlassSize(savedGlass);

    if (savedData) {
      const data = JSON.parse(savedData);
      // Reset if it's a new day
      if (data.date === today) {
        setWaterIntake(data.intake || 0);
        setLastDrink(data.lastDrink ? new Date(data.lastDrink) : null);
      } else {
        // New day - reset
        saveHydrationData(0, null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveHydrationData = (intake, lastDrinkTime) => {
    const today = new Date().toDateString();
    const data = {
      date: today,
      intake: intake,
      lastDrink: lastDrinkTime ? lastDrinkTime.toISOString() : null
    };
    localStorage.setItem('hydrationData', JSON.stringify(data));
    onUpdate(data);
  };

  const saveGoal = (oz) => {
    if (oz <= 0 || oz > 500) {
      toast.error("Daily goal must be between 1 and 500 oz");
      return;
    }
    setDailyGoalOz(oz);
    localStorage.setItem('hydrationGoalOz', String(oz));
    toast.success(`Daily goal set to ${oz}oz`);
  };

  const saveGlass = (oz) => {
    if (oz <= 0 || oz > 64) return;
    setGlassSize(oz);
    localStorage.setItem('hydrationGlassOz', String(oz));
  };

  const addWater = (oz) => {
    const amt = oz || glassSize;
    if (amt <= 0) return;
    const newIntake = waterIntake + amt;
    const now = new Date();
    setWaterIntake(newIntake);
    setLastDrink(now);
    saveHydrationData(newIntake, now);

    // Celebrate milestones
    if (newIntake >= dailyGoalOz && waterIntake < dailyGoalOz) {
      toast.success("🎉 Daily hydration goal reached! You're a hydration hero!");
    } else if (newIntake >= dailyGoalOz / 2 && waterIntake < dailyGoalOz / 2) {
      toast.success("💧 Halfway to your hydration goal!");
    } else {
      toast.success(`+${amt}oz water logged`);
    }
  };

  const handleCustomAdd = () => {
    const amt = parseInt(customAmount, 10);
    if (!amt || amt <= 0 || amt > 128) {
      toast.error("Enter a valid amount (1–128 oz)");
      return;
    }
    addWater(amt);
    setCustomAmount("");
    setShowCustomAdd(false);
  };

  const removeWater = (oz) => {
    const amt = oz || glassSize;
    const newIntake = Math.max(0, waterIntake - amt);
    setWaterIntake(newIntake);
    saveHydrationData(newIntake, lastDrink);
  };

  const resetWater = () => {
    if (waterIntake > 0 && !window.confirm("Reset water intake to 0 for today?")) return;
    setWaterIntake(0);
    setLastDrink(null);
    saveHydrationData(0, null);
    toast.info("Hydration tracker reset");
  };

  const progressPercent = Math.min((waterIntake / dailyGoalOz) * 100, 100);
  const glassesConsumed = Math.floor(waterIntake / glassSize);
  const timeSinceLastDrink = lastDrink
    ? Math.floor((new Date() - lastDrink) / 60000)
    : null;

  // Compact version for inline display
  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Button
          size="sm"
          variant="outline"
          className="h-8 px-2 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
          onClick={() => addWater()}
          data-testid="add-water-compact-btn"
        >
          <Droplets className="w-4 h-4 mr-1" />
          <span className="text-xs">{waterIntake}oz</span>
        </Button>
        <Progress value={progressPercent} className="w-16 h-2" />
      </div>
    );
  }

  return (
    <Card className={`bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30 ${className}`} data-testid="hydration-tracker">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
              <Droplets className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-bold text-white">Hydration Tracker</h3>
              <p className="text-xs text-muted-foreground">Stay hydrated — we want you at your best.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {progressPercent >= 100 && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <Trophy className="w-3 h-3 mr-1" />
                Goal Reached!
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-cyan-400 hover:bg-cyan-500/20 h-8 w-8 p-0"
              onClick={() => setShowSettings((v) => !v)}
              data-testid="hydration-settings-btn"
              title="Hydration settings"
            >
              <Settings2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Settings panel */}
        {showSettings && (
          <div
            className="mb-4 p-3 bg-background/50 border border-cyan-500/20 rounded-lg space-y-3"
            data-testid="hydration-settings-panel"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-cyan-300">Customize</h4>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-muted-foreground"
                onClick={() => setShowSettings(false)}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>

            {/* Daily goal picker */}
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Daily goal (oz)</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {PRESET_GOALS.map((oz) => (
                  <Button
                    key={oz}
                    size="sm"
                    variant={dailyGoalOz === oz ? "default" : "outline"}
                    className={dailyGoalOz === oz ? "bg-cyan-600 hover:bg-cyan-500 h-7 px-2 text-xs" : "border-cyan-500/30 text-cyan-300 h-7 px-2 text-xs"}
                    onClick={() => saveGoal(oz)}
                    data-testid={`goal-preset-${oz}-btn`}
                  >
                    {oz}oz
                  </Button>
                ))}
              </div>
              <div className="flex gap-1.5">
                <input
                  type="number"
                  min="1"
                  max="500"
                  inputMode="numeric"
                  placeholder="Custom goal"
                  value={customGoalInput}
                  onChange={(e) => setCustomGoalInput(e.target.value)}
                  className="flex-1 h-8 px-2 text-xs bg-background border border-cyan-500/30 rounded text-white placeholder:text-muted-foreground/60 focus:outline-none focus:border-cyan-400"
                  data-testid="custom-goal-input"
                />
                <Button
                  size="sm"
                  className="bg-cyan-600 hover:bg-cyan-500 h-8 text-xs"
                  onClick={() => {
                    const v = parseInt(customGoalInput, 10);
                    if (v) {
                      saveGoal(v);
                      setCustomGoalInput("");
                    }
                  }}
                  data-testid="custom-goal-save-btn"
                >
                  Set
                </Button>
              </div>
            </div>

            {/* Glass size picker */}
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Default glass size (oz)</p>
              <div className="flex flex-wrap gap-1.5">
                {PRESET_GLASS.map((oz) => (
                  <Button
                    key={oz}
                    size="sm"
                    variant={glassSize === oz ? "default" : "outline"}
                    className={glassSize === oz ? "bg-cyan-600 hover:bg-cyan-500 h-7 px-2 text-xs" : "border-cyan-500/30 text-cyan-300 h-7 px-2 text-xs"}
                    onClick={() => saveGlass(oz)}
                    data-testid={`glass-preset-${oz}-btn`}
                  >
                    {oz}oz
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-2 bg-background/50 rounded-lg">
            <div className="text-2xl font-bold text-cyan-400" data-testid="water-oz-consumed">{waterIntake}</div>
            <div className="text-xs text-muted-foreground">oz consumed</div>
          </div>
          <div className="text-center p-2 bg-background/50 rounded-lg">
            <div className="text-2xl font-bold text-blue-400">{glassesConsumed}</div>
            <div className="text-xs text-muted-foreground">glasses ({glassSize}oz)</div>
          </div>
          <div className="text-center p-2 bg-background/50 rounded-lg">
            <div className="text-2xl font-bold text-white">
              {timeSinceLastDrink !== null ? `${timeSinceLastDrink}m` : '--'}
            </div>
            <div className="text-xs text-muted-foreground">since last drink</div>
          </div>
        </div>

        {/* Progress Bar */}
        {showGoal && (
          <div className="mb-4">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground">Daily Goal</span>
              <span className="text-cyan-400 font-medium">
                {waterIntake}/{dailyGoalOz}oz ({Math.round(progressPercent)}%)
              </span>
            </div>
            <Progress value={progressPercent} className="h-3" />
            <div className="flex justify-between text-xs mt-1">
              <span className="text-muted-foreground">0</span>
              <span className="text-muted-foreground flex items-center gap-1">
                <Target className="w-3 h-3" />
                Daily goal
              </span>
            </div>
          </div>
        )}

        {/* Quick Add Buttons */}
        <div className="flex gap-2 mb-2">
          <Button
            onClick={() => addWater(8)}
            className="flex-1 bg-cyan-600 hover:bg-cyan-500"
            data-testid="add-water-8oz-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            8oz
          </Button>
          <Button
            onClick={() => addWater(16)}
            variant="outline"
            className="flex-1 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
            data-testid="add-water-16oz-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            16oz
          </Button>
          <Button
            onClick={() => addWater(32)}
            variant="outline"
            className="flex-1 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
            data-testid="add-water-32oz-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            32oz
          </Button>
          <Button
            onClick={() => setShowCustomAdd((v) => !v)}
            variant="outline"
            className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20"
            data-testid="add-water-custom-toggle-btn"
            title="Custom amount"
          >
            <Plus className="w-4 h-4" />
            …
          </Button>
        </div>

        {/* Custom amount input */}
        {showCustomAdd && (
          <div className="flex gap-2 mb-2" data-testid="custom-add-panel">
            <input
              type="number"
              min="1"
              max="128"
              inputMode="numeric"
              placeholder="oz"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCustomAdd(); }}
              autoFocus
              className="flex-1 h-9 px-3 text-sm bg-background border border-cyan-500/40 rounded text-white placeholder:text-muted-foreground/60 focus:outline-none focus:border-cyan-400"
              data-testid="custom-water-amount-input"
            />
            <Button
              onClick={handleCustomAdd}
              className="bg-cyan-600 hover:bg-cyan-500"
              data-testid="add-water-custom-confirm-btn"
            >
              Log
            </Button>
          </div>
        )}

        {/* Adjust / Reset */}
        <div className="flex gap-2">
          <Button
            onClick={() => removeWater()}
            variant="ghost"
            size="sm"
            className="flex-1 text-muted-foreground hover:text-red-400"
            disabled={waterIntake === 0}
            data-testid="remove-water-btn"
          >
            <Minus className="w-4 h-4 mr-1" />
            Remove {glassSize}oz
          </Button>
          <Button
            onClick={resetWater}
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-yellow-400"
            data-testid="reset-water-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Reset
          </Button>
        </div>

        {/* Hydration Tips */}
        {timeSinceLastDrink !== null && timeSinceLastDrink > 30 && (
          <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-xs text-yellow-400 flex items-center gap-1">
              <Droplets className="w-3 h-3" />
              It's been {timeSinceLastDrink} minutes since your last drink. Time to hydrate!
            </p>
          </div>
        )}

        {/* Stay Hydrated Reminder */}
        <div className="mt-3 p-2 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
          <p className="text-[11px] text-cyan-300/80 leading-relaxed text-center">
            <strong className="text-cyan-300">Stay hydrated.</strong> We want to make you better — every sip counts.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default HydrationTracker;
